import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  Injectable,
  Injector,
  NgModule,
  inject,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { isPublicRoute } from './shared/public-routes';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgbModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';

import { AbstractComponent } from './components/abstract/abstract.component';
import { ParentComponent } from './components/parent/parent.component';
import { ChildComponent } from './components/child/child.component';

import { DashboardModule } from './modules/dashboard/dashboard.module';
import { ShipmentsModule } from './modules/shipments/shipments.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  HttpXhrBackend,
} from '@angular/common/http';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { ApiInterceptor } from './interceptors/api/api.interceptor';
import { StorageService } from './services/storage/storage.service';
import { environment } from 'src/environments/environment';
import { ApiService } from './services/api/api.service';
import { AuthService } from './services/auth/auth.service';
import { FooterComponent } from './components/footer/footer.component';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { FeedbackformComponent } from './components/feedbackform/feedbackform.component';
import { JobStatusBarComponent } from './components/job-status-bar/job-status-bar.component';
import 'ag-grid-enterprise';
import { PodAuthInterceptor } from './interceptors/api/pod-auth-interceptor';
import { CookieService } from 'ngx-cookie-service';
import { UserModeInitService } from './services/customs/user-mode-init/user-mode-init.service';
import { FinancialsModule } from './modules/financials/financials.module';
import { RateManagerModule } from './modules/rate-manager/rate-manager.module';

export function initAuth(api: ApiService, injector: Injector) {
  return () =>
    new Promise((resolve, reject) => {
      const httpClient: HttpClient = new HttpClient(
        new HttpXhrBackend({
          build: () => new XMLHttpRequest(),
        }),
      );

      /* ---------- EARLY EXIT FOR PUBLIC / AUTH PAGES ---------- */
      const isExcluded = isPublicRoute(); // ✅ new helper
      const isOnAuthPage = location.href.startsWith(environment.auth_url);

      if (isExcluded || isOnAuthPage) {
        resolve(true); // let Angular finish bootstrapping
        return; // ← the only return you need here
      }

      if (StorageService.getCookie('AUTH').length > 0) {
        httpClient
          .post(environment.auth_validate, {
            token: StorageService.getCookie('AUTH'),
          })
          .subscribe({
            next: (response: any) => {
              if (!response['valid']) {
                StorageService.deleteCookie('AUTH');
                if (!location.search.startsWith('?auth_token=')) {
                  localStorage.setItem('route', location.href);
                }
                location.href = environment.auth_url;
                return;
              }
              if (response['renew']) {
                StorageService.deleteCookie('AUTH');
                if (!location.search.startsWith('?auth_token=')) {
                  localStorage.setItem('route', location.href);
                }
                location.href = response['renew_url'];
                return;
              }
              resolve(true);
            },
            error: (error: any) => {
              location.href = environment.auth_failed;
            },
          });

        return;
      } else if (location.search.startsWith('?auth_token=')) {
        const urlParams = new URLSearchParams(location.search);
        const authToken = urlParams.get('auth_token');
        httpClient
          .post(environment.auth_callback, { otp: authToken })
          .subscribe({
            next: (response: any) => {
              if (!('token' in response)) {
                location.href = environment.auth_failed;
                return;
              }
              StorageService.setCookie('AUTH', response['token']);
              let url: string | null = localStorage.getItem('route');
              if (url != null) {
                localStorage.removeItem('route');
                location.href = url;
                return;
              }
              resolve(true);
            },
            error: (error: any) => {
              location.href = environment.auth_failed;
            },
          });
        return;
      } else {
        localStorage.setItem('route', location.href);

        location.href = environment.auth_url;
      }
    });
}

export function initializeUserMode(userModeInitService: UserModeInitService) {
  return () => userModeInitService.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SidebarComponent,
    AbstractComponent,
    ParentComponent,
    ChildComponent,
    SpinnerComponent,
    FooterComponent,
    ConfirmDialogComponent,
    InfoDialogComponent,
    FeedbackformComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgbModule,
    DashboardModule,
    ShipmentsModule,
    FinancialsModule,
    RateManagerModule,
    HttpClientModule,
    JobStatusBarComponent,
  ],
  providers: [
    CookieService,
    NgbActiveModal,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [ApiService, Injector],
      multi: true,
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeUserMode,
    //   deps: [UserModeInitService],
    //   multi: true,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PodAuthInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
